import React from 'react';
import { CheckCircleIcon, XCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import type { CommitResult } from '../types';

interface CommitOutcomeListProps {
  result: CommitResult;
  onRunPreviewAgain: () => void;
}

const CommitOutcomeList: React.FC<CommitOutcomeListProps> = ({ result, onRunPreviewAgain }) => (
  <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
    {/* Header */}
    <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
      <div className="flex items-center gap-3">
        <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
          Commit results
        </h3>
        <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
          <CheckCircleIcon className="w-3.5 h-3.5" />
          {result.successCount} succeeded
        </span>
        {result.errorCount > 0 && (
          <span className="flex items-center gap-1 text-xs font-semibold text-red-600 dark:text-red-400">
            <XCircleIcon className="w-3.5 h-3.5" />
            {result.errorCount} failed
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={onRunPreviewAgain}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        <ArrowPathIcon className="w-3.5 h-3.5" />
        Run preview again
      </button>
    </div>

    {/* Outcome rows */}
    <ul className="divide-y divide-gray-100 dark:divide-gray-800">
      {result.processed.map((outcome) => (
        <li key={outcome.rowNumber} className="flex items-start gap-3 px-5 py-3">
          {outcome.success ? (
            <CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
          ) : (
            <XCircleIcon className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
          )}
          <div className="min-w-0">
            <span className="text-sm font-mono text-gray-600 dark:text-gray-400">
              Row #{outcome.rowNumber}
            </span>
            {outcome.error && (
              <p className="text-xs mt-0.5 text-red-600 dark:text-red-400">
                {outcome.error}
              </p>
            )}
          </div>
        </li>
      ))}
    </ul>
  </div>
);

export default CommitOutcomeList;
